import { useEffect, useRef } from 'react'

/**
 * Attaches an IntersectionObserver to a sentinel element.
 * Calls `onLoadMore` when the sentinel scrolls into view.
 * No-ops when `hasMore` is false or `isFetching` is true.
 */
export function useInfiniteScroll({
  hasMore,
  isFetching,
  onLoadMore,
  rootMargin = '0px 0px 120px 0px',
}: {
  hasMore: boolean
  isFetching: boolean
  onLoadMore: () => void
  rootMargin?: string
}) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hasMore || isFetching) return
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore()
      },
      { rootMargin }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, isFetching, onLoadMore, rootMargin])

  return sentinelRef
}
